import { IDetailCellRendererParams } from "ag-grid-community";
import { useState } from "react";
import {
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiTextArea,
} from "@inscopix/ideas-eui";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { captureException } from "@sentry/react";
import { JsonValue, SetNonNullable } from "type-fest";
import { ADVANCED_PARAM_DETAIL_HEIGHT } from "./ParameterEditorDetailRenderer";
import { isToolParamV3 } from "types/json-schemas/type_guards";
import { ParamsEditorRowDatum } from "components/ToolEditor/ToolEditor.types";
import { ToolEditorStoreState } from "components/ToolEditor/Provider/store/ToolEditorProvider.store";
import assert from "assert";

interface DetailRendererAdvancedProps extends SetNonNullable<
  Pick<IDetailCellRendererParams<ParamsEditorRowDatum>, "data">,
  "data"
> {
  onClose: () => void;
  overwriteParam?: ToolEditorStoreState["overwriteParam"];
  editMode?: boolean;
}
export const DetailRendererAdvanced = ({
  data,
  onClose,
  overwriteParam,
  editMode = true,
}: DetailRendererAdvancedProps) => {
  const [param, setParam] = useState(JSON.stringify(data, null, 2));
  const [isValid, setIsValid] = useState(true);

  const handleSave = () => {
    try {
      assert(overwriteParam !== undefined, "overwriteParam is undefined");
      const parsedParam = JSON.parse(param) as JsonValue;
      const isValid = isToolParamV3(parsedParam);

      if (isValid) {
        overwriteParam(data.key, parsedParam);
      } else {
        throw Error();
      }
      onClose();
    } catch (err) {
      captureException(
        "Failed to parse JSON as tool param when expected to be valid",
        {
          extra: { param },
        },
      );
      addUtilityToastFailure("Failed to save param");
    }
  };
  return (
    <EuiFlexGroup style={{ width: "100%", padding: 25 }}>
      <EuiFlexItem>
        <EuiTextArea
          isInvalid={!isValid}
          disabled={!editMode}
          value={param}
          style={{
            height: ADVANCED_PARAM_DETAIL_HEIGHT - 50,
            border: "2px solid lightgray",
          }}
          onChange={(e) => {
            const newValue = e.target.value;
            try {
              const isValid = isToolParamV3(JSON.parse(newValue));
              setIsValid(isValid);
              /**
               * Try/Catch necessary because parsing JSON
               */
            } catch {
              setIsValid(false);
            } finally {
              setParam(newValue);
            }
          }}
          fullWidth
        />
      </EuiFlexItem>
      {editMode ? (
        <EuiFlexItem grow={false}>
          <EuiFlexGroup
            style={{ width: 200 }}
            direction="column"
            gutterSize="s"
          >
            <EuiFlexItem grow={false}>
              <EuiButton isDisabled={!isValid} onClick={handleSave}>
                Save
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton color="text" onClick={onClose}>
                Cancel
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      ) : (
        <EuiFlexItem grow={false}>
          <EuiButton color="text" onClick={onClose}>
            Close
          </EuiButton>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );
};
